import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startupDataService, type StartupDataRow } from '@ideoz/core';
import { Button, ConfirmDialog, toastError, toastSuccess } from '@ideoz/ui';
import { RolePicker } from './RolePicker';

/**
 * The accounts people created on the startup's prototypes (the built-in `User` data): set a
 * user's role, or remove a user (US 598). Removed users go to Recently deleted like any record.
 */
export function StartupUsersPanel({
  projectId,
  onChanged,
}: {
  projectId: string;
  onChanged?: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [users, setUsers] = useState<StartupDataRow[] | null>(null);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState<StartupDataRow | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await startupDataService.users(projectId);
      setUsers(rows);
      setRoles(Object.fromEntries(rows.map((r) => [r.record.id, String(r.record.role ?? '')])));
    } catch (error) {
      toastError(error instanceof Error ? error.message : t('startupData.users.loadFailed'));
      setUsers([]);
    }
  }, [projectId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (work: () => Promise<void>, done: string) => {
    setBusy(true);
    try {
      await work();
      toastSuccess(done);
      onChanged?.();
      await load();
    } catch (error) {
      toastError(error instanceof Error ? error.message : t('startupData.genericError'));
    } finally {
      setBusy(false);
    }
  };

  if (users === null)
    return <p className="p-6 text-sm text-muted-foreground">{t('startupData.users.loading')}</p>;
  if (users.length === 0) {
    return <p className="p-6 text-sm text-muted-foreground">{t('startupData.users.none')}</p>;
  }

  return (
    <div className="overflow-auto px-5 py-4">
      <p className="mb-3 max-w-[65ch] text-xs text-muted-foreground">
        {t('startupData.users.roleHelp')} {t('startupData.roles.hint')}
      </p>
      <table className="w-full min-w-max border-collapse text-sm">
        <thead>
          <tr className="border-b border-border/60 text-left text-xs text-muted-foreground">
            <th scope="col" className="px-2 py-2 font-medium">
              {t('startupData.users.email')}
            </th>
            <th scope="col" className="px-2 py-2 font-medium">
              {t('startupData.users.name')}
            </th>
            <th scope="col" className="px-2 py-2 font-medium">
              {t('startupData.users.role')}
            </th>
            <th scope="col" className="px-2 py-2 font-medium">
              {t('startupData.users.joined')}
            </th>
            <th scope="col" className="px-2 py-2">
              <span className="sr-only">{t('startupData.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((row) => {
            const id = row.record.id;
            const role = roles[id] ?? '';
            const changedRole = role !== String(row.record.role ?? '');
            return (
              <tr key={id} className="border-b border-border/40">
                <td className="px-2 py-1.5">{String(row.record.email ?? '')}</td>
                <td className="px-2 py-1.5">{String(row.record.name ?? '')}</td>
                <td className="px-2 py-1.5">
                  <RolePicker
                    label={t('startupData.users.roleOf', { user: String(row.record.email ?? id) })}
                    value={role}
                    usedRoles={users.map((u) => String(u.record.role ?? ''))}
                    onChange={(next) => setRoles((r) => ({ ...r, [id]: next }))}
                  />
                  {changedRole ? (
                    <Button
                      size="sm"
                      className="ml-2"
                      disabled={busy}
                      onClick={() =>
                        void act(
                          () => startupDataService.setRole(projectId, id, role),
                          t('startupData.users.roleSaved')
                        )
                      }
                    >
                      {t('startupData.save')}
                    </Button>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-xs text-muted-foreground">
                  {new Date(row.createdAt).toLocaleDateString()}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setRemoving(row)}
                  >
                    {t('startupData.users.remove')}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <ConfirmDialog
        open={removing !== null}
        title={t('startupData.users.removeTitle')}
        description={t('startupData.users.removeDescription', {
          email: String(removing?.record.email ?? ''),
        })}
        confirmLabel={t('startupData.users.removeConfirm')}
        variant="danger"
        isLoading={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() =>
          act(async () => {
            if (removing) await startupDataService.removeUser(projectId, removing.record.id);
            setRemoving(null);
          }, t('startupData.users.removed'))
        }
      />
    </div>
  );
}

export default StartupUsersPanel;
